import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, MessageSquarePlus, Lightbulb } from "lucide-react";
import { api } from "@/lib/api";
import { REVIEWS } from "@/constants/testIds";

export const Reviews = ({ onOpenReview, onOpenSuggestion }) => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api
      .get("/reviews")
      .then((res) => setReviews(res.data))
      .catch(() => setReviews([]));
  }, []);

  return (
    <section
      id="reviews"
      data-testid={REVIEWS.section}
      className="py-24 md:py-32 px-6 md:px-10 max-w-7xl mx-auto"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
      >
        <div>
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#D97736] mb-4">
            Families Who Trust Us
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0F2520]">
            Reviews &amp; Suggestions
          </h2>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="review-open-button-inline"
            onClick={onOpenReview}
            className="rounded-full bg-[#0F2520] text-white px-5 py-3 text-sm font-semibold flex items-center gap-2 hover:bg-[#D97736] transition-colors"
          >
            <MessageSquarePlus className="w-4 h-4" /> Leave a Review
          </button>
          <button
            data-testid="suggestion-open-button-inline"
            onClick={onOpenSuggestion}
            className="rounded-full border-2 border-[#0F2520] text-[#0F2520] px-5 py-3 text-sm font-semibold flex items-center gap-2 hover:bg-[#0F2520] hover:text-white transition-colors"
          >
            <Lightbulb className="w-4 h-4" /> Suggest
          </button>
        </div>
      </motion.div>

      {reviews.length === 0 ? (
        <p className="text-[#0F2520]/60 font-light">Be the first to share your experience with us.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <motion.div
              key={r.id}
              data-testid={REVIEWS.card(r.id)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-white rounded-[1.5rem] p-6 border border-[#DCD7CE]"
            >
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, idx) => (
                  <Star
                    key={idx}
                    className={`w-4 h-4 ${idx < r.rating ? "fill-[#F4D06F] text-[#F4D06F]" : "text-[#DCD7CE]"}`}
                  />
                ))}
              </div>
              <p className="text-[#0F2520]/80 font-light leading-relaxed mb-4">"{r.message}"</p>
              <p className="font-display font-semibold text-[#0F2520]">— {r.name}</p>
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
};
